import AlbumIcon from '@mui/icons-material/Album';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useEditContext } from 'react-admin';
import { Else, If, Then } from 'react-if';

import type {
  IndustryAlbumRow,
  IndustryContributor,
  RoleDetails,
} from '@/Api/studioServerTypes';
import { authHttpClient } from '@/Auth/authProvider';
import OverflowChipsCell from '@/Components/OverflowChipsCell';

export default function IndustryCreditSection(): JSX.Element {
  const { record, isPending } = useEditContext<IndustryContributor & { id: string }>();
  const contributorId = (record?.id ?? '').trim();
  const {
    data: rows = [],
    isLoading: isRowsLoading,
    isError: hasError,
  } = useQuery<IndustryAlbumRow[]>({
    queryKey: ['industry-contributor-albums', contributorId],
    enabled: !!contributorId,
    queryFn: async () => {
      const response = await authHttpClient(`/api/admin/discography/industry-contributors/${contributorId}/albums`);
      return response.json as IndustryAlbumRow[];
    },
  });
  const isTableLoading = isPending || isRowsLoading;
  const renderContributorChips = (items?: (RoleDetails | null)[] | null): JSX.Element => {
    const contributorIdByItem: Record<string, string> = {};
    const avatarByItem: Record<string, string> = {};
    const names: string[] = [];

    (items ?? []).forEach((item) => {
      const contributorName = (item?.Name ?? '').trim();
      if (!contributorName) return;

      const normalizedName = contributorName.toLowerCase();
      const targetContributorId = (item?.IndustryContributorIdentifier ?? '').trim();
      const assignedResource = (item?.AssignedResource ?? '').trim();

      names.push(contributorName);
      if (targetContributorId) contributorIdByItem[normalizedName] = targetContributorId;
      if (assignedResource) avatarByItem[normalizedName] = assignedResource;
    });

    return (
      <OverflowChipsCell
        value={names}
        maxVisible={2}
        avatarByItem={avatarByItem}
        onItemClick={(name) => {
          const targetId = contributorIdByItem[name.trim().toLowerCase()];
          if (!targetId) return;
          window.location.assign(`/industry-contributors/${targetId}`);
        }}
      />
    );
  };

  return (
    <Box
      mb={3} width={{ xs: '100%', md: '90%' }}
      p={2} border={1}
      borderRadius={2} borderColor='grey.400'
    >
      <Typography
        variant='h6' gutterBottom
        display='flex' alignItems='center'
        gap={1}
      >
        <AlbumIcon />
        Albums
      </Typography>

      <Paper variant='outlined' sx={{ p: 1, borderRadius: 3 }}>
        <If condition={isTableLoading}>
          <Then>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          </Then>
          <Else>
            <If condition={hasError}>
              <Then>
                <Typography color='error' sx={{ p: 2 }}>
                  Failed to load album credits.
                </Typography>
              </Then>
              <Else>
                <If condition={!rows.length}>
                  <Then>
                    <Typography color='text.secondary' sx={{ p: 2 }}>
                      No credits found for this contributor.
                    </Typography>
                  </Then>
                  <Else>
                    <TableContainer
                      sx={{
                        maxHeight: 620, overflowY: 'auto',
                      }}
                    >
                      <Table
                        stickyHeader
                        size='small'
                        sx={{
                          tableLayout: 'fixed',
                          width: '100%',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          verticalAlign: 'top',
                        }}
                      >
                        <TableHead sx={{
                          '& .MuiTableCell-root': {
                            backgroundColor: 'background.paper',
                          },
                        }}
                        >
                          <TableRow>
                            <TableCell>Genre</TableCell>
                            <TableCell>Artist</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Roles</TableCell>
                            <TableCell>Mixing Engineer</TableCell>
                            <TableCell>Producers</TableCell>
                            <TableCell>Mastering Engineer</TableCell>
                            <TableCell>Sterling</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {rows.map((row) => (
                            <TableRow
                              key={row.IndustryAlbumIdentifier ?? `${row.Artist ?? ''}-${row.Title ?? ''}`}
                              sx={row.IsSterling ? {
                                'backgroundColor': '#3B362F',
                                '& .MuiTableCell-root': {
                                  color: '#fff',
                                  borderBottomColor: 'rgba(255,255,255,0.25)',
                                },
                                '& .MuiChip-root': {
                                  backgroundColor: 'rgba(255,255,255,0.14)',
                                  color: '#fff',
                                },
                              } : undefined}
                            >
                              <TableCell>
                                <OverflowChipsCell value={row.Genre ?? []} maxVisible={2} />
                              </TableCell>
                              <TableCell>{row.Artist ?? '—'}</TableCell>
                              <TableCell>{row.Title ?? '—'}</TableCell>
                              <TableCell>
                                <OverflowChipsCell value={row.Roles ?? []} maxVisible={2} />
                              </TableCell>
                              <TableCell>
                                {renderContributorChips(row.MixingEngineer)}
                              </TableCell>
                              <TableCell>
                                {renderContributorChips(row.Producer)}
                              </TableCell>
                              <TableCell>
                                {renderContributorChips(row.MasteringEngineer)}
                              </TableCell>
                              <TableCell>
                                <If condition={!!row.IsSterling}>
                                  <Then>
                                    <Box
                                      component='img'
                                      src='/favicon-32x32.png'
                                      alt='Sterling'
                                      sx={{ width: 22, height: 22, display: 'block' }}
                                    />
                                  </Then>
                                  <Else>-</Else>
                                </If>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Else>
                </If>
              </Else>
            </If>
          </Else>
        </If>
      </Paper>
    </Box>
  );
}
